import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar as CalIcon, Plus, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface SectionRow { id: string; name: string; section_type: string; color: string | null; is_resource: boolean; }
interface EventRow { id: string; section_id: string; title: string; description: string | null; location: string | null; starts_at: string; ends_at: string; all_day: boolean; attendees: unknown; }

/** /calendar — utilizable week view */
export function CalendarPage() {
  const { t } = useTranslation();
  const [sections, setSections] = useState<SectionRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [formOpen, setFormOpen] = useState(false);
  const [defaultDate, setDefaultDate] = useState<Date | null>(null);

  const weekEnd = new Date(weekStart); weekEnd.setDate(weekEnd.getDate() + 7);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiClient.get<SectionRow[]>('/calendar/sections'),
      apiClient.get<EventRow[]>('/calendar/events', { params: { from: weekStart.toISOString(), to: weekEnd.toISOString() } }),
    ])
      .then(([{ data: s }, { data: e }]) => { setSections(s); setEvents(e); })
      .finally(() => setLoading(false));
  }, [weekStart, refresh]);

  // Build a 7-day grid
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });

  function eventsOnDay(d: Date): EventRow[] {
    return events.filter(e => {
      const s = new Date(e.starts_at);
      return s.toDateString() === d.toDateString();
    }).sort((a, b) => a.starts_at.localeCompare(b.starts_at));
  }

  function sectionColor(id: string): string {
    const s = sections.find(x => x.id === id);
    return s?.color || '#3b82f6';
  }

  function openCreate(d: Date) {
    setDefaultDate(d);
    setFormOpen(true);
  }

  return (
    <div className="space-y-3 p-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><CalIcon className="h-5 w-5" />{t('calendar.title')}</h1>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="text-sm font-medium">{formatRange(weekStart, weekEnd)}</span>
          <Button size="sm" variant="outline" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}>
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => setWeekStart(startOfWeek(new Date()))}>{t('calendar.today')}</Button>
          <Button size="sm" onClick={() => { setDefaultDate(new Date()); setFormOpen(true); }}><Plus className="mr-1 h-3.5 w-3.5" />{t('calendar.newEvent')}</Button>
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-muted-foreground">{t('common.loading')}</div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {days.map(d => (
            <div key={d.toISOString()} className="rounded-lg border bg-card p-2 min-h-40">
              <button onClick={() => openCreate(d)} className="w-full text-left">
                <div className="mb-2 text-xs font-medium text-muted-foreground">{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
                <div className={`text-lg font-bold ${isToday(d) ? 'text-primary' : ''}`}>{d.getDate()}</div>
              </button>
              <div className="mt-2 space-y-1">
                {eventsOnDay(d).map(ev => (
                  <div key={ev.id} className="rounded px-2 py-1 text-xs cursor-pointer" style={{ background: sectionColor(ev.section_id) + '22', borderLeft: `3px solid ${sectionColor(ev.section_id)}` }}>
                    <div className="font-medium truncate">{ev.title}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {ev.all_day ? t('calendar.allDay') : new Date(ev.starts_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {ev.location && <div className="text-[10px] truncate text-muted-foreground"><MapPin className="inline h-2.5 w-2.5" /> {ev.location}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <CreateEventForm
          sections={sections}
          defaultDate={defaultDate ?? new Date()}
          onClose={() => setFormOpen(false)}
          onCreated={() => { setRefresh(x => x + 1); setFormOpen(false); }}
        />
      )}
    </div>
  );
}

function CreateEventForm({ sections, defaultDate, onClose, onCreated }: { sections: SectionRow[]; defaultDate: Date; onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const dStart = new Date(defaultDate); dStart.setHours(9, 0, 0, 0);
  const dEnd = new Date(defaultDate); dEnd.setHours(10, 0, 0, 0);
  const [startsAt, setStartsAt] = useState(toLocalInput(dStart));
  const [endsAt, setEndsAt] = useState(toLocalInput(dEnd));
  const [sectionId, setSectionId] = useState(sections[0]?.id ?? '');
  const [allDay, setAllDay] = useState(false);
  const [attendees, setAttendees] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCreateSection, setShowCreateSection] = useState(sections.length === 0);
  const [sectionName, setSectionName] = useState('');

  async function submit() {
    if (!sectionId && !sectionName) return;
    setSaving(true);
    try {
      let sid = sectionId;
      if (!sid) {
        const { data } = await apiClient.post<{ id: string }>('/calendar/sections', { name: sectionName, type: 'user' });
        sid = data.id;
      }
      const attendeeList = attendees.split(',').map(s => s.trim()).filter(Boolean).map(email => ({ email }));
      const { data: ev } = await apiClient.post<{ id: string }>('/calendar/events', {
        sectionId: sid, title, description, location,
        startsAt: new Date(startsAt).toISOString(), endsAt: new Date(endsAt).toISOString(),
        allDay, attendees: attendeeList,
      });
      if (attendeeList.length > 0 && ev?.id) {
        await apiClient.post(`/calendar/events/${ev.id}/notify-attendees`).catch(() => {});
      }
      onCreated();
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg border bg-card p-6" onClick={e => e.stopPropagation()}>
        <h3 className="mb-3 text-lg font-semibold">{t('calendar.newEvent')}</h3>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium">{t('calendar.eventTitle')}</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} required />
          </div>
          {showCreateSection ? (
            <div>
              <label className="mb-1 block text-sm font-medium">{t('calendar.createSection')}</label>
              <Input value={sectionName} onChange={e => setSectionName(e.target.value)} placeholder="Mi calendario" />
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium">{t('calendar.section')}</label>
              <select value={sectionId} onChange={e => setSectionId(e.target.value)} className="w-full rounded-md border bg-background px-3 py-2 text-sm">
                {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <button onClick={() => setShowCreateSection(true)} className="mt-1 text-xs text-primary hover:underline">{t('calendar.createNewSection')}</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="mb-1 block text-sm font-medium">{t('calendar.startsAt')}</label>
              <Input type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">{t('calendar.endsAt')}</label>
              <Input type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={allDay} onChange={e => setAllDay(e.target.checked)} />
            {t('calendar.allDay')}
          </label>
          <div>
            <label className="mb-1 block text-sm font-medium">{t('calendar.location')}</label>
            <Input value={location} onChange={e => setLocation(e.target.value)} placeholder="Sala A / Zoom" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">{t('calendar.attendees')}</label>
            <Input value={attendees} onChange={e => setAttendees(e.target.value)} placeholder="email1@x.com, email2@y.com" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">{t('calendar.description')}</label>
            <textarea rows={3} value={description} onChange={e => setDescription(e.target.value)} className="w-full rounded-md border bg-background px-3 py-2 text-sm" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>{t('common.cancel')}</Button>
            <Button size="sm" onClick={submit} disabled={saving || !title.trim()}>{saving ? t('common.saving') : t('common.save')}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function startOfWeek(d: Date): Date {
  const x = new Date(d);
  const day = x.getDay();
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
}
function isToday(d: Date): boolean { return d.toDateString() === new Date().toDateString(); }
function formatRange(a: Date, b: Date): string {
  return `${a.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${new Date(b.getTime()-1).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}
function toLocalInput(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
